import type { z } from 'zod';

/**
 * The HTTP client every Nest app uses for its own backend (from Pay Console's client): same-origin calls through the
 * edge (`/<app>/api`), the HttpOnly session cookie (no token is ever visible here), the one session's CSRF token on
 * every write, and every response checked against its schema — one that does not match fails as BAD_RESPONSE
 * instead of reaching a screen. A 401 from any backend means the one Nest session has ended.
 */

/* Error codes and the problem shape come from the shared contract (tolerant of codes a newer backend adds). */
export { ERROR_CODES, Problem, type ErrorCode } from '@infinix/contract';
import { Problem, type ErrorCode } from '@infinix/contract';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly requestId: string | undefined;
  readonly fields: Readonly<Record<string, string>>;

  constructor(problem: Problem) {
    super(problem.title);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code;
    this.requestId = problem.requestId;
    this.fields = problem.fields ?? {};
  }
}

/* ---- the one session's CSRF token and end-of-session signal (set by the shell session) ---------------------- */

let csrf: string | null = null;
/** Who the token belongs to (person + organization), so a retry never runs as someone else. */
let owner: string | null = null;
let lastServerContactMs = Date.now();
const endedListeners = new Set<() => void>();
type Reread = () => Promise<{ csrf: string; owner: string } | null>;
let reread: Reread | null = null;

/** The shell session sets the token it read from /account/api/session, whose it is, and how to read it again. */
export function setCsrf(token: string | null, tokenOwner: string | null = null, readAgain?: Reread): void {
  csrf = token;
  owner = tokenOwner;
  if (readAgain) reread = readAgain;
}

function endSession(): void {
  csrf = null;
  owner = null;
  for (const l of endedListeners) l();
}

/** Called whenever a backend says the session is gone. */
export function onSessionEnded(listener: () => void): () => void {
  endedListeners.add(listener);
  return () => endedListeners.delete(listener);
}

/** Time of the last successful call — when a backend last re-stamped the session. Not user activity. */
export function lastServerContact(): number {
  return lastServerContactMs;
}

const UNREACHABLE: Problem = { status: 0, code: 'UPSTREAM_UNAVAILABLE', title: "Can't reach Nest. Check your connection and try again." };
const BAD_RESPONSE: Problem = { status: 0, code: 'BAD_RESPONSE', title: 'Nest sent a response this page cannot read. Reload and try again.' };

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export interface CallOptions {
  /** false for calls that start or end a session: their 401 is an answer, not the end of the session. */
  sessionCall?: boolean;
  csrfRetried?: boolean;
  /** Extra request headers, e.g. an Idempotency-Key for an action that must not run twice. */
  headers?: Readonly<Record<string, string>>;
}

export interface ApiClient {
  /** A call whose JSON body must match `schema`. */
  request<S extends z.ZodType>(schema: S, method: Method, path: string, body?: unknown, opts?: CallOptions): Promise<z.infer<S>>;
  /** A call answered with no body (204). */
  send(method: Method, path: string, body?: unknown, opts?: CallOptions): Promise<void>;
}

/** A client for one backend, e.g. createApiClient('/payments/api'). */
export function createApiClient(base: string): ApiClient {
  async function call(method: Method, path: string, body: unknown, opts: CallOptions): Promise<Response> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (method !== 'GET' && csrf) headers['x-csrf-token'] = csrf;
    for (const [k, val] of Object.entries(opts.headers ?? {})) headers[k.toLowerCase()] = val;

    let res: Response;
    try {
      res = await fetch(`${base}${path}`, {
        method,
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiError(UNREACHABLE);
    }
    if (res.ok) {
      lastServerContactMs = Date.now();
      return res;
    }

    // The CSRF token is stale or not loaded yet: read the current one once and retry, so writes are not silently
    // lost — but only for the same person in the same organization. If someone else has signed in on this browser
    // (another tab), the write is never replayed as them: this session is over.
    if (res.status === 403 && method !== 'GET' && !opts.csrfRetried && reread) {
      const problem = Problem.safeParse(await res.clone().json().catch(() => null));
      if (problem.success && problem.data.code === 'CSRF_FAILED') {
        let current: Awaited<ReturnType<Reread>>;
        try {
          current = await reread();
        } catch {
          // Couldn't check (network or backend down): say so, and leave the session alone.
          throw new ApiError(UNREACHABLE);
        }
        if (current && owner && current.owner === owner) {
          csrf = current.csrf;
          return call(method, path, body, { ...opts, csrfRetried: true });
        }
        if (!opts.sessionCall) endSession();
        throw new ApiError({ status: 401, code: 'AUTH_REQUIRED', title: 'Your session changed. Sign in again to continue.' });
      }
    }

    const parsed = Problem.safeParse(await res.json().catch(() => null));
    const err = new ApiError(
      parsed.success
        ? parsed.data
        : { status: res.status, code: res.status >= 500 ? 'UPSTREAM_UNAVAILABLE' : 'INTERNAL', title: 'Something went wrong. Try again.' },
    );
    if (err.code === 'AUTH_REQUIRED' && !opts.sessionCall) endSession();
    throw err;
  }

  return {
    async request(schema, method, path, body, opts = {}) {
      const res = await call(method, path, body, opts);
      const parsed = schema.safeParse(await res.json().catch(() => undefined));
      if (!parsed.success) {
        // Path and issue paths only: never response data.
        console.error('API contract mismatch', method, `${base}${path}`, parsed.error.issues.map((i) => i.path.join('.')));
        throw new ApiError({ ...BAD_RESPONSE, requestId: res.headers.get('x-request-id') ?? undefined });
      }
      return parsed.data;
    },
    async send(method, path, body, opts = {}) {
      await call(method, path, body, opts);
    },
  };
}

/** Builds a query string from defined, non-empty values. */
export function queryString(params: object, skip: (k: string, v: unknown) => boolean = () => false): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '' && !skip(k, v)) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : '';
}
